import { useMemo, useState } from 'react'

import { SnippetEditorSheetHost } from '@/features/snippets/SnippetEditorSheetHost'
import { SheetContainerProvider } from '@/components/layout/SheetContainerContext'
import { ContentPanelAreaInner } from '@/components/layout/ContentPanelAreaInner'
import { ShelfAnnouncer } from '@/components/panel/tabBar/ShelfAnnouncer'
import { CloseTabDialog } from '@/components/layout/CloseTabDialog'
import { useActiveTabId } from '@/hooks/useActiveTabId'

// Geometry lives entirely in AuthLayout's grid; this component measures
// nothing. Inner is memoized — `<ContentPanelAreaInner />` is a stable child
// element with no props, so React.memo bails on every parent re-render. That,
// plus a SheetContainerContext value that no longer carries a width, keeps the
// heavy tab subtree off the sidebar-drag render path.
export function ContentPanelArea() {
  // Callback ref (useState setter) — not useRef — so SheetContainerContext
  // consumers re-render when the DOM node attaches. Required for sheets to
  // localize correctly inside this area.
  const [sheetContainer, setSheetContainer] = useState<HTMLElement | null>(null)

  // Localized sheets all portal into the single container below, which lives
  // OUTSIDE the per-tab trees. This rule now serves the keepAlive routes only.
  //
  // A sheet or popover opened in a NON-keepAlive tab does not survive
  // backgrounding: the tab unmounts and takes the portal with it, so reopening
  // the tab reopens a closed sheet.
  //
  // On a keepAlive tab the old trap remains: an open sheet on a now-inactive
  // tab keeps painting over the active tab — and can't be closed, since a
  // hidden <Activity> freezes its subtree. Each portaled sheet is stamped with
  // its owning tab id (data-tab-owner via sheet.tsx); hide any whose owner
  // isn't the active tab. State is preserved, so the sheet reappears (and is
  // interactive again) when its tab becomes active. This is a DOM-only lever,
  // so it works without re-rendering the frozen subtree.
  //
  // Popovers (popover.tsx) hit the same trap but portal to document.body, OUTSIDE
  // this container — so the hide rule below is intentionally NOT scoped to the
  // container; it matches any data-tab-owner stamp wherever it portals.
  const activeId = useActiveTabId()
  const activeTabOwnerCss = useMemo(
    () => (activeId ?? '').replace(/\\/g, '\\\\').replace(/"/g, '\\"'),
    [activeId]
  )

  return (
    // Outer div is the sheet portal target:
    //   * overflow-hidden clips the slide-in animation of localized <Sheet>s
    //     so the sheet visually emerges from this container's right edge
    //     (which aligns with the chat sidebar's left edge).
    //   * No vertical margin — sheet (position: absolute inset-y-0) fills the
    //     full height of the main content area.
    //
    // Inner div carries `my-3` so the table/tab content keeps its 12px top &
    // bottom breathing room without affecting the sheet's geometry.
    <SheetContainerProvider container={sheetContainer}>
      <div
        ref={setSheetContainer}
        data-slot="content-panel-area"
        className="flex-1 min-h-0 flex flex-col relative overflow-hidden">
        <style>{`[data-tab-owner]:not([data-tab-owner="${activeTabOwnerCss}"]){display:none !important;}`}</style>
        <div className="flex-1 min-h-0 flex flex-col my-3">
          <ContentPanelAreaInner />
        </div>
        {/* Opened from a snippet block in any script editor. Mounted here, not
            in a tab, so it portals into this container and slides in from the
            content panel's edge like every other sheet — and so it survives the
            frozen <Activity> subtree the triggering editor lives in. */}
        <SnippetEditorSheetHost />
        {/* Mounted outside the tab trees so it stays registered with assistive
            tech across tab opens and closes. */}
        <ShelfAnnouncer />
        {/* Same reason: a close prompt rendered inside the tab it is about
            would freeze with that tab's <Activity> subtree. */}
        <CloseTabDialog />
      </div>
    </SheetContainerProvider>
  )
}
